import {
  Application,
  Container,
  Graphics,
  Text,
  TextStyle,
  WebGPURenderer,
} from 'pixi.js';
import { buildRenderShape } from '@lakaran/engine';
import type { CadEditor } from '../editor/editor';
import { BBox, Vec2, entityPoint, entityTextSpec, type EntityId, type TextSpec } from '@lakaran/geometry';
import { COLORS } from '../theme';

export type RenderBackend = 'webgpu' | 'webgl';

export async function isWebGpuAvailable(): Promise<boolean> {
  const gpu = (navigator as unknown as {
    gpu?: { requestAdapter(): Promise<unknown | null> };
  }).gpu;
  if (!gpu) return false;
  try {
    const adapter = await gpu.requestAdapter();
    return adapter !== null && adapter !== undefined;
  } catch {
    return false;
  }
}

interface Bucket {
  strokes: Graphics;
  fills: Graphics;
  color: number;
  widthPx: number;
}

interface TextEntry {
  text: Text;
  spec: TextSpec;
  color: number;
}

const TEXT_FONT = 'Inter, "Segoe UI", system-ui, sans-serif';
const CHORD_TOLERANCE_PX = 0.3;

/**
 * Pixi v8 renderer. Geometry is tessellated in world millimetres and lives inside a
 * Y-up container, so panning only touches the container transform and zoom only forces
 * a rebuild when the LOD band changes. Overlays and text live in screen space.
 */
export class CadRenderer {
  readonly backend: RenderBackend;

  private readonly app: Application;
  private readonly canvas: HTMLCanvasElement;
  private readonly host: HTMLElement;
  private readonly editor: CadEditor;

  private readonly grid = new Container();
  private readonly gridMinor = new Graphics();
  private readonly gridMajor = new Graphics();
  private readonly gridAxes = new Graphics();

  private readonly world = new Container();
  private readonly overlayWorld = new Container();
  private readonly selectionGraphics = new Graphics();
  private readonly draftGraphics = new Graphics();
  private readonly previewGraphics = new Graphics();
  private readonly textLayer = new Container();
  private readonly hud = new Graphics();

  private readonly buckets = new Map<string, Bucket>();
  private readonly previewBuckets = new Map<string, Bucket>();
  private readonly texts = new Map<EntityId, TextEntry>();

  private readonly disposers: Array<() => void> = [];
  private readonly resizeObserver: ResizeObserver;

  private dirtyGeometry = true;
  private builtBand = Number.NaN;
  private builtScale = 1;
  private frameHandle = 0;

  private constructor(
    app: Application,
    host: HTMLElement,
    canvas: HTMLCanvasElement,
    editor: CadEditor,
  ) {
    this.app = app;
    this.host = host;
    this.canvas = canvas;
    this.editor = editor;
    this.backend = app.renderer instanceof WebGPURenderer ? 'webgpu' : 'webgl';

    this.grid.addChild(this.gridMinor, this.gridMajor, this.gridAxes);
    this.overlayWorld.addChild(
      this.previewGraphics,
      this.selectionGraphics,
      this.draftGraphics,
    );
    app.stage.addChild(this.grid, this.world, this.overlayWorld, this.textLayer, this.hud);

    this.disposers.push(
      editor.events.on('document', () => {
        this.dirtyGeometry = true;
        this.schedule();
      }),
      editor.events.on('view', () => this.schedule()),
      editor.events.on('overlay', () => this.schedule()),
    );

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(host);
    this.handleResize();
  }

  static async create(host: HTMLElement, editor: CadEditor): Promise<CadRenderer> {
    const canvas = document.createElement('canvas');
    const app = new Application();
    await app.init({
      canvas,
      preference: 'webgpu',
      antialias: true,
      autoDensity: true,
      resolution: window.devicePixelRatio || 1,
      backgroundColor: COLORS.background,
      width: Math.max(1, host.clientWidth),
      height: Math.max(1, host.clientHeight),
      powerPreference: 'high-performance',
    });
    host.appendChild(canvas);
    const renderer = new CadRenderer(app, host, canvas, editor);
    renderer.schedule();
    return renderer;
  }

  get canvasElement(): HTMLCanvasElement {
    return this.canvas;
  }

  destroy(): void {
    if (this.frameHandle) cancelAnimationFrame(this.frameHandle);
    this.frameHandle = 0;
    this.resizeObserver.disconnect();
    for (const dispose of this.disposers) dispose();
    this.disposers.length = 0;
    this.texts.clear();
    this.buckets.clear();
    this.app.destroy(true, { children: true });
  }

  // ------------------------------------------------------------------ frames

  private schedule(): void {
    if (this.frameHandle) return;
    this.frameHandle = requestAnimationFrame(() => {
      this.frameHandle = 0;
      this.update();
    });
  }

  private update(): void {
    const viewport = this.editor.viewport;
    const band = viewport.lodBand();
    this.syncCamera();

    if (this.dirtyGeometry || band !== this.builtBand) {
      this.rebuildGeometry();
      this.rebuildText();
      this.builtBand = band;
      this.builtScale = viewport.scale;
      this.dirtyGeometry = false;
    }

    this.updateTextTransforms();
    this.drawGrid();
    this.drawOverlay();
    this.app.render();
  }

  /** Re-syncs the canvas with its host element (used after layout changes). */
  refreshSize(): void {
    this.handleResize();
  }

  private handleResize(): void {
    const width = Math.max(1, Math.floor(this.host.clientWidth));
    const height = Math.max(1, Math.floor(this.host.clientHeight));
    if (width === this.editor.viewport.width && height === this.editor.viewport.height) return;
    this.app.renderer.resize(width, height);
    this.editor.resize(width, height);
    this.dirtyGeometry = true;
  }

  private syncCamera(): void {
    const viewport = this.editor.viewport;
    const scale = viewport.scale;
    for (const container of [this.world, this.overlayWorld]) {
      container.scale.set(scale, -scale);
      container.position.set(
        viewport.width / 2 - viewport.center.x * scale,
        viewport.height / 2 + viewport.center.y * scale,
      );
    }
  }

  // ---------------------------------------------------------------- geometry

  private rebuildGeometry(): void {
    const viewport = this.editor.viewport;
    const doc = this.editor.doc;
    const tolerance = viewport.pxToWorld(CHORD_TOLERANCE_PX);
    const bounds = viewport.bounds.expand(viewport.pxToWorld(64));
    const touched = new Set<Bucket>();

    for (const entity of doc.queryVisible(bounds)) {
      const layer = doc.getLayer(entity.layerId);
      if (!layer) continue;
      const shape = buildRenderShape(entity, layer, { chordTolerance: tolerance });
      if (!shape) continue;

      const key = `${shape.color}:${shape.lineWidthPx}`;
      let bucket = this.buckets.get(key);
      if (!bucket) {
        bucket = {
          strokes: new Graphics(),
          fills: new Graphics(),
          color: shape.color,
          widthPx: shape.lineWidthPx,
        };
        this.world.addChild(bucket.fills, bucket.strokes);
        this.buckets.set(key, bucket);
      }
      if (!touched.has(bucket)) {
        bucket.strokes.clear();
        bucket.fills.clear();
        bucket.strokes.visible = true;
        bucket.fills.visible = false;
        touched.add(bucket);
      }
      for (const path of shape.paths) {
        this.appendPath(bucket.strokes, path.points, path.closed);
      }
      for (const fill of shape.fills) {
        if (fill.points.length < 3) continue;
        bucket.fills.poly(fill.points.map((p) => ({ x: p.x, y: p.y })), true);
        bucket.fills.visible = true;
      }
    }

    for (const bucket of this.buckets.values()) {
      if (!touched.has(bucket)) {
        bucket.strokes.visible = false;
        bucket.fills.visible = false;
        continue;
      }
      bucket.strokes.stroke({
        width: Math.max(bucket.widthPx / viewport.scale, 0.01),
        color: bucket.color,
        alpha: 1,
        cap: 'round',
        join: 'round',
      });
      if (bucket.fills.visible) {
        bucket.fills.fill({ color: bucket.color, alpha: 1 });
      }
    }
  }

  private appendPath(graphics: Graphics, points: readonly Vec2[], closed: boolean): void {
    if (points.length < 2) return;
    const first = points[0]!;
    graphics.moveTo(first.x, first.y);
    for (let i = 1; i < points.length; i++) {
      const p = points[i]!;
      graphics.lineTo(p.x, p.y);
    }
    if (closed) graphics.lineTo(first.x, first.y);
  }

  // -------------------------------------------------------------------- text

  private rebuildText(): void {
    const viewport = this.editor.viewport;
    const doc = this.editor.doc;
    const bounds = viewport.bounds.expand(viewport.pxToWorld(128));
    const seen = new Set<EntityId>();

    for (const entity of doc.queryVisible(bounds)) {
      const spec = entityTextSpec(entity);
      if (!spec) continue;
      const layer = doc.getLayer(entity.layerId);
      if (!layer) continue;
      seen.add(entity.id);

      const color = entity.color ?? layer.color;
      let entry = this.texts.get(entity.id);
      if (!entry) {
        const text = new Text({
          text: spec.text,
          style: new TextStyle({
            fontFamily: TEXT_FONT,
            fontSize: Math.max(4, spec.height * viewport.scale),
            fill: color,
            letterSpacing: 0,
          }),
        });
        this.textLayer.addChild(text);
        entry = { text, spec, color };
        this.texts.set(entity.id, entry);
      } else {
        entry.spec = spec;
        if (entry.text.text !== spec.text) entry.text.text = spec.text;
        if (entry.color !== color) {
          entry.color = color;
          entry.text.style.fill = color;
        }
      }
      entry.text.style.fontSize = Math.max(spec.height * viewport.scale, 4);
      entry.text.alpha = 1;
      entry.text.visible = true;
    }

    for (const [id, entry] of this.texts) {
      if (seen.has(id)) continue;
      if (!doc.has(id)) {
        entry.text.destroy();
        this.texts.delete(id);
      } else {
        entry.text.visible = false;
      }
    }
  }

  private updateTextTransforms(): void {
    const viewport = this.editor.viewport;
    const ratio = viewport.scale / (this.builtScale || viewport.scale);
    for (const entry of this.texts.values()) {
      if (!entry.text.visible) continue;
      const spec = entry.spec;
      const screen = viewport.worldToScreen(spec.position);
      const descent = spec.height * 0.22 * viewport.scale;

      if (spec.anchor === 'center') entry.text.anchor.set(0.5, 1);
      else if (spec.anchor === 'right') entry.text.anchor.set(1, 1);
      else entry.text.anchor.set(0, 1);

      let y = screen.y;
      if (spec.valign === 'baseline') y += descent;
      else if (spec.valign === 'middle') y -= spec.height * 0.5 * viewport.scale;
      else if (spec.valign === 'top') y -= spec.height * viewport.scale;

      entry.text.position.set(screen.x, y);
      entry.text.rotation = -spec.rotation;
      entry.text.scale.set(spec.widthFactor * ratio, ratio);
    }
  }

  // -------------------------------------------------------------------- grid

  private drawGrid(): void {
    this.gridMinor.clear();
    this.gridMajor.clear();
    this.gridAxes.clear();

    const editor = this.editor;
    if (!editor.settings.grid.visible) return;

    const viewport = editor.viewport;
    const bounds = viewport.bounds;
    let step = Math.max(editor.settings.grid.size, 1e-6);
    while (step * viewport.scale < 8) step *= 10;
    while (step * viewport.scale > 160) step /= 10;

    const majorEvery = Math.max(1, Math.round(editor.settings.grid.majorEvery));
    const cols = Math.ceil(bounds.width / step) + 1;
    const rows = Math.ceil(bounds.height / step) + 1;
    if (cols > 4000 || rows > 4000) return;

    const startX = Math.ceil(bounds.minX / step) * step;
    const startY = Math.ceil(bounds.minY / step) * step;

    for (let i = 0; i <= cols; i++) {
      const x = startX + i * step;
      const major = Math.round(x / step) % majorEvery === 0;
      const g = major ? this.gridMajor : this.gridMinor;
      const top = viewport.worldToScreen(Vec2.of(x, bounds.maxY));
      const bottom = viewport.worldToScreen(Vec2.of(x, bounds.minY));
      g.moveTo(top.x, top.y).lineTo(bottom.x, bottom.y);
    }
    for (let i = 0; i <= rows; i++) {
      const y = startY + i * step;
      const major = Math.round(y / step) % majorEvery === 0;
      const g = major ? this.gridMajor : this.gridMinor;
      const left = viewport.worldToScreen(Vec2.of(bounds.minX, y));
      const right = viewport.worldToScreen(Vec2.of(bounds.maxX, y));
      g.moveTo(left.x, left.y).lineTo(right.x, right.y);
    }

    const xLeft = viewport.worldToScreen(Vec2.of(bounds.minX, 0));
    const xRight = viewport.worldToScreen(Vec2.of(bounds.maxX, 0));
    const yTop = viewport.worldToScreen(Vec2.of(0, bounds.maxY));
    const yBottom = viewport.worldToScreen(Vec2.of(0, bounds.minY));

    this.gridMinor.stroke({ width: 1, color: COLORS.gridMinor, alpha: 1 });
    this.gridMajor.stroke({ width: 1, color: COLORS.gridMajor, alpha: 1 });

    this.gridAxes.moveTo(xLeft.x, xLeft.y).lineTo(xRight.x, xRight.y);
    this.gridAxes.stroke({ width: 1, color: COLORS.axisX, alpha: 0.9 });
    this.gridAxes.moveTo(yTop.x, yTop.y).lineTo(yBottom.x, yBottom.y);
    this.gridAxes.stroke({ width: 1, color: COLORS.axisY, alpha: 0.9 });
  }

  // ----------------------------------------------------------------- overlay

  private drawOverlay(): void {
    const viewport = this.editor.viewport;
    const doc = this.editor.doc;
    const tolerance = viewport.pxToWorld(CHORD_TOLERANCE_PX);

    this.selectionGraphics.clear();
    for (const id of this.editor.selection) {
      const entity = doc.get(id);
      if (!entity) continue;
      const layer = doc.getLayer(entity.layerId);
      if (!layer) continue;
      const shape = buildRenderShape(entity, layer, { chordTolerance: tolerance });
      if (!shape) continue;
      for (const path of shape.paths) {
        this.appendPath(this.selectionGraphics, path.points, path.closed);
      }
    }
    this.selectionGraphics.stroke({
      width: 2.2 / viewport.scale,
      color: COLORS.selection,
      alpha: 1,
      cap: 'round',
      join: 'round',
    });

    this.previewGraphics.clear();
    // The preview keeps the colour, weight and pattern the steps author — a card that says "red,
    // 3 px, dashed" must look like that while you tune it, not like a generic orange sketch.
    const previewTouched = new Set<Bucket>();
    for (const entity of this.editor.preview) {
      const layer = doc.getLayer(entity.layerId);
      if (!layer) continue;
      const shape = buildRenderShape(entity, layer, { chordTolerance: tolerance });
      if (!shape) continue;

      const key = `${shape.color}:${shape.lineWidthPx}`;
      let bucket = this.previewBuckets.get(key);
      if (!bucket) {
        bucket = {
          strokes: new Graphics(),
          fills: new Graphics(),
          color: shape.color,
          widthPx: shape.lineWidthPx,
        };
        this.previewGraphics.addChild(bucket.fills, bucket.strokes);
        this.previewBuckets.set(key, bucket);
      }
      if (!previewTouched.has(bucket)) {
        bucket.strokes.clear();
        bucket.fills.clear();
        bucket.strokes.visible = true;
        bucket.fills.visible = false;
        previewTouched.add(bucket);
      }
      for (const path of shape.paths) {
        this.appendPath(bucket.strokes, path.points, path.closed);
      }
      for (const fill of shape.fills) {
        if (fill.points.length < 3) continue;
        bucket.fills.poly(fill.points.map((p) => ({ x: p.x, y: p.y })), true);
        bucket.fills.visible = true;
      }
    }
    for (const bucket of this.previewBuckets.values()) {
      if (!previewTouched.has(bucket)) {
        bucket.strokes.visible = false;
        bucket.fills.visible = false;
        continue;
      }
      bucket.strokes.stroke({
        width: Math.max(bucket.widthPx / viewport.scale, 0.01),
        color: bucket.color,
        alpha: 0.9,
        cap: 'round',
        join: 'round',
      });
      if (bucket.fills.visible) bucket.fills.fill({ color: bucket.color, alpha: 0.9 });
    }

    this.draftGraphics.clear();
    for (const entity of this.editor.draft) {
      const layer = doc.getLayer(entity.layerId);
      if (!layer) continue;
      const shape = buildRenderShape(entity, layer, { chordTolerance: tolerance });
      if (!shape) continue;
      for (const path of shape.paths) {
        this.appendPath(this.draftGraphics, path.points, path.closed);
      }
    }
    this.draftGraphics.stroke({
      width: 1.6 / viewport.scale,
      color: COLORS.draft,
      alpha: 0.95,
      cap: 'round',
      join: 'round',
    });

    this.drawHud();
  }

  private drawHud(): void {
    const editor = this.editor;
    const viewport = editor.viewport;
    const hud = this.hud;
    hud.clear();

    const cursor = editor.cursorScreen;

    hud
      .moveTo(0, cursor.y)
      .lineTo(viewport.width, cursor.y)
      .moveTo(cursor.x, 0)
      .lineTo(cursor.x, viewport.height)
      .stroke({ width: 1, color: COLORS.crosshair, alpha: 0.2 });

    const start = editor.selectionStart;
    const end = editor.selectionEnd;
    if (start && end) {
      const a = viewport.worldToScreen(start);
      const b = viewport.worldToScreen(end);
      const x = Math.min(a.x, b.x);
      const y = Math.min(a.y, b.y);
      const w = Math.abs(a.x - b.x);
      const h = Math.abs(a.y - b.y);
      if (w > 1 && h > 1) {
        const crossing = b.x < a.x;
        hud.rect(x, y, w, h).stroke({
          width: 1,
          color: crossing ? COLORS.crossingSelect : COLORS.windowSelect,
          alpha: 0.9,
        });
      }
    }

    const snap = editor.activeSnap;
    if (snap) {
      const marker = viewport.worldToScreen(editor.cursorWorld);
      const r = 5;
      switch (snap.type) {
        case 'endpoint':
          hud.rect(marker.x - r, marker.y - r, r * 2, r * 2);
          break;
        case 'midpoint':
          hud
            .moveTo(marker.x, marker.y - r)
            .lineTo(marker.x + r, marker.y + r)
            .lineTo(marker.x - r, marker.y + r)
            .closePath();
          break;
        case 'center':
          hud.circle(marker.x, marker.y, r);
          break;
        case 'intersection':
          hud
            .moveTo(marker.x - r, marker.y - r)
            .lineTo(marker.x + r, marker.y + r)
            .moveTo(marker.x + r, marker.y - r)
            .lineTo(marker.x - r, marker.y + r);
          break;
        case 'perpendicular':
          hud
            .moveTo(marker.x - r, marker.y + r)
            .lineTo(marker.x + r, marker.y + r)
            .moveTo(marker.x, marker.y - r)
            .lineTo(marker.x, marker.y + r);
          break;
        case 'tangent':
          hud
            .circle(marker.x, marker.y, r)
            .moveTo(marker.x - r, marker.y - r)
            .lineTo(marker.x + r, marker.y - r);
          break;
        default:
          hud.circle(marker.x, marker.y, 2);
          break;
      }
      hud.stroke({ width: 1.4, color: COLORS.snap, alpha: 1 });
    }

    this.drawConstraintGlyphs(hud);
  }

  /** Small squares mark constrained handles; filled dots mark anchored points. */
  private drawConstraintGlyphs(hud: Graphics): void {
    const editor = this.editor;
    const doc = editor.doc;
    if (doc.constraints.size === 0) return;
    const viewport = editor.viewport;
    const box = BBox.fromPoints([
      viewport.screenToWorld(Vec2.of(0, 0)),
      viewport.screenToWorld(Vec2.of(viewport.width, viewport.height)),
    ]).expand(viewport.pxToWorld(12));

    const seen = new Set<string>();
    for (const constraint of doc.constraints.values()) {
      if (constraint.kind === 'anchor') {
        const point = viewport.worldToScreen(constraint.at);
        hud.circle(point.x, point.y, 1.6);
        continue;
      }
      const refs =
        constraint.kind === 'coincident' || constraint.kind === 'distance'
          ? [constraint.point, constraint.target]
          : constraint.kind === 'fix' || constraint.kind === 'pointOnEdge'
            ? [constraint.point]
            : [];
      for (const ref of refs) {
        const key = `${ref.entityId}\u0000${ref.handle}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const entity = doc.get(ref.entityId);
        if (!entity) continue;
        const world = entityPoint(entity, ref.handle);
        if (!world || !box.containsPoint(world)) continue;
        const screen = viewport.worldToScreen(world);
        hud.rect(screen.x - 3.5, screen.y - 3.5, 7, 7);
      }
    }
    hud.stroke({ width: 1, color: COLORS.constraint, alpha: 0.9 });
    hud.fill({ color: COLORS.constraint, alpha: 0.9 });
  }
}
